import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Users, 
  Plus, 
  CheckCircle, 
  XCircle, 
  Edit, 
  Trash2, 
  ExternalLink,
  Clock,
  AlertCircle,
  Upload,
  Image,
  Loader2,
  Facebook
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { API } from '../App';



const FacebookGroupsAdmin = () => {
  const { language } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Get token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };
  const [dialogMode, setDialogMode] = useState(null); // 'add', 'edit', 'delete', 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    member_count: '',
    image_url: '',
    keywords: '',
    submitter_email: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/admin/facebook-groups`, {
        headers: getAuthHeaders()
      });
      setGroups(response.data.groups || []);
      setStats(response.data.stats || { total: 0, pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Error loading groups');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      member_count: '',
      image_url: '',
      keywords: '',
      submitter_email: 'admin@middleman.com'
    });
    setDialogMode('add');
  };

  const handleOpenEdit = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      url: group.url || group.facebook_url || '',
      member_count: group.member_count || '',
      image_url: group.image_url || group.cover_image || '',
      keywords: Array.isArray(group.keywords) ? group.keywords.join(', ') : (group.keywords || ''),
      submitter_email: group.submitter_email || 'admin@middleman.com'
    });
    setDialogMode('edit');
  };

  const handleOpenDelete = (group) => {
    setSelectedGroup(group);
    setDialogMode('delete');
  };

  const handleOpenReject = (group) => {
    setSelectedGroup(group);
    setRejectReason('');
    setDialogMode('reject');
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedGroup(null);
    setRejectReason('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'fr' ? 'Type de fichier non autorisé' : 'File type not allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (max 5MB)' : 'File too large (max 5MB)');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/facebook-groups/upload-image`, uploadData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        }
      });

      if (response.data.url) {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
        toast.success(language === 'fr' ? 'Image téléversée' : 'Image uploaded');
      }
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du téléversement' : 'Upload error');
      toast.error(message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert keywords string -> array before sending to backend
      const payload = {
        ...formData,
        keywords: (formData.keywords || '')
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0),
      };
      if (dialogMode === 'add') {
        await axios.post(`${API}/admin/facebook-groups`, payload, {
          headers: getAuthHeaders()
        });
        toast.success(language === 'fr' ? 'Groupe créé avec succès' : 'Group created successfully');
      } else if (dialogMode === 'edit') {
        await axios.put(`${API}/admin/facebook-groups/${selectedGroup.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success(language === 'fr' ? 'Groupe mis à jour' : 'Group updated');
      }
      handleCloseDialog();
      fetchGroups();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error';
      toast.error(message);
    }
  };

  const handleApprove = async (group) => {
    try {
      await axios.post(`${API}/admin/facebook-groups/${group.id}/approve`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(language === 'fr' ? 'Groupe approuvé' : 'Group approved');
      fetchGroups();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'approbation' : 'Error approving group');
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API}/admin/facebook-groups/${selectedGroup.id}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(language === 'fr' ? 'Groupe rejeté' : 'Group rejected');
      handleCloseDialog();
      fetchGroups();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du rejet' : 'Error rejecting group');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/facebook-groups/${selectedGroup.id}`, {
        headers: getAuthHeaders()
      });
      toast.success(language === 'fr' ? 'Groupe supprimé' : 'Group deleted');
      handleCloseDialog();
      fetchGroups();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting group');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> {language === 'fr' ? 'Approuvé' : 'Approved'}</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> {language === 'fr' ? 'En attente' : 'Pending'}</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> {language === 'fr' ? 'Rejeté' : 'Rejected'}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-2 md:gap-4 mb-4">
        <Card className="p-2 md:p-0">
          <CardContent className="p-2 md:pt-6 md:px-6">
            <div className="flex flex-col items-center text-center md:flex-row md:text-left md:justify-between">
              <Users className="h-5 w-5 md:h-8 md:w-8 text-blue-500 mb-1 md:mb-0 md:order-2" />
              <div>
                <p className="text-[10px] md:text-sm text-gray-500">{language === 'fr' ? 'Total' : 'Total'}</p>
                <p className="text-lg md:text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="p-2 md:p-0">
          <CardContent className="p-2 md:pt-6 md:px-6">
            <div className="flex flex-col items-center text-center md:flex-row md:text-left md:justify-between">
              <Clock className="h-5 w-5 md:h-8 md:w-8 text-yellow-500 mb-1 md:mb-0 md:order-2" />
              <div>
                <p className="text-[10px] md:text-sm text-gray-500">{language === 'fr' ? 'Attente' : 'Pending'}</p>
                <p className="text-lg md:text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="p-2 md:p-0">
          <CardContent className="p-2 md:pt-6 md:px-6">
            <div className="flex flex-col items-center text-center md:flex-row md:text-left md:justify-between">
              <CheckCircle className="h-5 w-5 md:h-8 md:w-8 text-green-500 mb-1 md:mb-0 md:order-2" />
              <div>
                <p className="text-[10px] md:text-sm text-gray-500">{language === 'fr' ? 'OK' : 'OK'}</p>
                <p className="text-lg md:text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="p-2 md:p-0">
          <CardContent className="p-2 md:pt-6 md:px-6">
            <div className="flex flex-col items-center text-center md:flex-row md:text-left md:justify-between">
              <XCircle className="h-5 w-5 md:h-8 md:w-8 text-red-500 mb-1 md:mb-0 md:order-2" />
              <div>
                <p className="text-[10px] md:text-sm text-gray-500">{language === 'fr' ? 'Rejeté' : 'Rejected'}</p>
                <p className="text-lg md:text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Groups Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {language === 'fr' ? 'Groupes Facebook' : 'Facebook Groups'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Gérer les groupes Facebook partenaires' : 'Manage partner Facebook groups'}
            </CardDescription>
          </div>
          <Button onClick={handleOpenAdd}>
            <Plus className="h-4 w-4 mr-2" />
            {language === 'fr' ? 'Ajouter' : 'Add'}
          </Button>
        </CardHeader>
        <CardContent>
          {/* Mobile view - Cards */}
          <div className="md:hidden space-y-3">
            {groups.length === 0 ? (
              <p className="text-center py-8 text-gray-500">
                {language === 'fr' ? 'Aucun groupe pour le moment' : 'No groups yet'}
              </p>
            ) : (
              groups.map((group) => {
                // Check if image_url is a valid image URL (not a Facebook share link)
                const imageUrl = group.image_url || group.cover_image;
                const isValidImage = imageUrl && (
                  imageUrl.startsWith('/uploads') || 
                  imageUrl.startsWith('http') && (
                    imageUrl.includes('.jpg') || 
                    imageUrl.includes('.jpeg') || 
                    imageUrl.includes('.png') || 
                    imageUrl.includes('.webp') ||
                    imageUrl.includes('.gif') ||
                    imageUrl.includes('cloudinary') ||
                    imageUrl.includes('imgur') ||
                    imageUrl.includes('images')
                  )
                );
                
                return (
                  <div key={group.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-start gap-3">
                      {isValidImage ? (
                        <img 
                          src={imageUrl.startsWith('/') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` 
                            : imageUrl} 
                          alt={group.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-16 h-16 rounded-lg bg-blue-100 items-center justify-center flex-shrink-0 ${isValidImage ? 'hidden' : 'flex'}`}>
                        <Facebook className="h-8 w-8 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{group.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{group.member_count || '-'} membres</span>
                          {getStatusBadge(group.status)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Description complète */}
                    {group.description && (
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        {group.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between pt-2 border-t">
                      <a 
                        href={group.url || group.facebook_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {language === 'fr' ? 'Voir le groupe' : 'View group'}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      <div className="flex items-center gap-1">
                        {group.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 hover:text-green-700 h-8 w-8 p-0"
                              onClick={() => handleApprove(group)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                              onClick={() => handleOpenReject(group)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-8 w-8 p-0"
                          onClick={() => handleOpenEdit(group)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                          onClick={() => handleOpenDelete(group)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop view - Table */}
          <Table className="hidden md:table">
            <TableHeader>
              <TableRow>
                <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                <TableHead>{language === 'fr' ? 'Membres' : 'Members'}</TableHead>
                <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                <TableHead>{language === 'fr' ? 'Soumis par' : 'Submitted by'}</TableHead>
                <TableHead className="text-right">{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    {language === 'fr' ? 'Aucun groupe pour le moment' : 'No groups yet'}
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {(group.image_url || group.cover_image) ? (
                          <img 
                            src={(group.image_url || group.cover_image).startsWith('/') 
                              ? `${process.env.REACT_APP_BACKEND_URL}${group.image_url || group.cover_image}` 
                              : (group.image_url || group.cover_image)} 
                            alt={group.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Image className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{group.name}</p>
                          <a 
                            href={group.url || group.facebook_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {language === 'fr' ? 'Voir le groupe' : 'View group'}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{group.member_count || '-'}</TableCell>
                    <TableCell>{getStatusBadge(group.status)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {group.submitter_email || group.created_by || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {group.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleApprove(group)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleOpenReject(group)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenEdit(group)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleOpenDelete(group)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogMode === 'add' || dialogMode === 'edit'} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'add' 
                ? (language === 'fr' ? 'Ajouter un groupe' : 'Add group')
                : (language === 'fr' ? 'Modifier le groupe' : 'Edit group')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{language === 'fr' ? 'Nom *' : 'Name *'}</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">{language === 'fr' ? 'URL Facebook *' : 'Facebook URL *'}</Label>
              <Input
                id="url"
                name="url"
                type="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="https://www.facebook.com/groups/..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{language === 'fr' ? 'Description' : 'Description'}</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member_count">{language === 'fr' ? 'Nombre de membres' : 'Member count'}</Label>
              <Input
                id="member_count"
                name="member_count"
                value={formData.member_count}
                onChange={handleChange}
                placeholder="Ex: 10K, 50K, 100K+"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="keywords">
                {language === 'fr' ? 'Mots-clés (séparés par virgule)' : 'Keywords (comma-separated)'}
              </Label>
              <Input
                id="keywords"
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
                placeholder={language === 'fr' ? 'Ex: Canadiens, Hockey, Bell Centre' : 'Ex: Canadiens, Hockey, Bell Centre'}
                data-testid="fb-group-keywords-input"
              />
              <p className="text-xs text-gray-500">
                {language === 'fr'
                  ? "Le groupe apparaîtra sur la page d'un événement si son nom contient l'un de ces mots-clés."
                  : "The group will appear on an event page if its name contains any of these keywords."}
              </p>
            </div>
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Image du groupe' : 'Group image'}</Label>
              <div className="flex flex-col gap-3">
                {/* Current image preview */}
                {formData.image_url && (
                  <div className="relative w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={formData.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}` : formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                {/* Upload buttons */}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex-1"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {language === 'fr' ? 'Téléversement...' : 'Uploading...'}
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {language === 'fr' ? 'Téléverser une image' : 'Upload image'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button type="submit">
                {dialogMode === 'add' 
                  ? (language === 'fr' ? 'Créer' : 'Create')
                  : (language === 'fr' ? 'Enregistrer' : 'Save')
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={dialogMode === 'reject'} onOpenChange={handleCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {language === 'fr' ? 'Rejeter le groupe' : 'Reject group'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir rejeter "${selectedGroup?.name}" ?`
                : `Are you sure you want to reject "${selectedGroup?.name}"?`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Raison (optionnel)' : 'Reason (optional)'}</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={language === 'fr' ? 'Expliquez pourquoi...' : 'Explain why...'}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              {language === 'fr' ? 'Rejeter' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialogMode === 'delete'} onOpenChange={handleCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {language === 'fr' ? 'Supprimer le groupe' : 'Delete group'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer "${selectedGroup?.name}" ? Cette action est irréversible.`
                : `Are you sure you want to delete "${selectedGroup?.name}"? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacebookGroupsAdmin;
